import { Router, type IRouter } from "express";
import { db, accessRequestsTable, dealsTable, usersTable } from "@workspace/db";
import { eq } from "drizzle-orm";
import { CreateAccessRequestBody, UpdateAccessRequestBody } from "@workspace/api-zod";
import { requireAuth, requireRole } from "../middlewares/auth";

const router: IRouter = Router();

function formatRequest(
  req: typeof accessRequestsTable.$inferSelect,
  deal?: typeof dealsTable.$inferSelect | null,
  investor?: typeof usersTable.$inferSelect | null
) {
  return {
    id: req.id,
    dealId: req.dealId,
    investorUserId: req.investorUserId,
    message: req.message,
    status: req.status,
    createdAt: req.createdAt,
    deal: deal
      ? {
          id: deal.id,
          title: deal.title,
          area: deal.area,
          building: deal.building,
          unitType: deal.unitType,
          bedrooms: deal.bedrooms,
          bathrooms: deal.bathrooms,
          sizeSqft: deal.sizeSqft ? parseFloat(deal.sizeSqft) : null,
          askingPrice: parseFloat(deal.askingPrice),
          estimatedMarketPrice: parseFloat(deal.estimatedMarketPrice),
          discountPercentage: deal.discountPercentage ? parseFloat(deal.discountPercentage) : null,
          urgencyScore: deal.urgencyScore,
          urgencyReason: deal.urgencyReason,
          sellerTimeline: deal.sellerTimeline,
          propertyStatus: deal.propertyStatus,
          paymentPlanDetails: deal.paymentPlanDetails,
          brokerContact: deal.brokerContact,
          imageUrl: deal.imageUrl,
          submittedByUserId: deal.submittedByUserId,
          status: deal.status,
          isVipOnly: deal.isVipOnly,
          createdAt: deal.createdAt,
          submittedBy: null,
        }
      : null,
    investor: investor
      ? {
          id: investor.id,
          name: investor.name,
          email: investor.email,
          phone: investor.phone,
          role: investor.role,
          membership: investor.membership,
          stripeCustomerId: investor.stripeCustomerId,
          createdAt: investor.createdAt,
        }
      : null,
  };
}

router.get("/access-requests", requireAuth, async (req, res): Promise<void> => {
  const rows = await db
    .select({
      request: accessRequestsTable,
      deal: dealsTable,
      investor: usersTable,
    })
    .from(accessRequestsTable)
    .leftJoin(dealsTable, eq(accessRequestsTable.dealId, dealsTable.id))
    .leftJoin(usersTable, eq(accessRequestsTable.investorUserId, usersTable.id))
    .where(
      req.auth!.role === "admin"
        ? undefined
        : eq(accessRequestsTable.investorUserId, req.auth!.userId)
    );

  res.json(rows.map((r) => formatRequest(r.request, r.deal, r.investor)));
});

router.post("/access-requests", requireAuth, requireRole("investor"), async (req, res): Promise<void> => {
  const parsed = CreateAccessRequestBody.safeParse(req.body);
  if (!parsed.success) {
    res.status(400).json({ error: parsed.error.message });
    return;
  }

  const [request] = await db
    .insert(accessRequestsTable)
    .values({
      dealId: parsed.data.dealId,
      investorUserId: req.auth!.userId,
      message: parsed.data.message,
      status: "new",
    })
    .returning();

  const [deal] = await db.select().from(dealsTable).where(eq(dealsTable.id, request.dealId));
  const [investor] = await db.select().from(usersTable).where(eq(usersTable.id, request.investorUserId));

  res.status(201).json(formatRequest(request, deal, investor));
});

router.put("/access-requests/:id", requireAuth, requireRole("admin"), async (req, res): Promise<void> => {
  const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
  const id = parseInt(raw, 10);

  const parsed = UpdateAccessRequestBody.safeParse(req.body);
  if (!parsed.success) {
    res.status(400).json({ error: parsed.error.message });
    return;
  }

  const [request] = await db
    .update(accessRequestsTable)
    .set({ status: parsed.data.status })
    .where(eq(accessRequestsTable.id, id))
    .returning();

  if (!request) {
    res.status(404).json({ error: "Access request not found" });
    return;
  }

  const [deal] = await db.select().from(dealsTable).where(eq(dealsTable.id, request.dealId));
  const [investor] = await db.select().from(usersTable).where(eq(usersTable.id, request.investorUserId));

  res.json(formatRequest(request, deal, investor));
});

export default router;
